import { createHeardRegistry } from '@didto/domain';
import { useEffect, useState } from 'react';
import { useDirectActions } from '@/app/directActions';

/* ── Screen wake lock ───────────────────────────────────────────────────────────────────── */

/** The display must not dim while it is the kitchen's only view of the queue. */
export function useWakeLock(enabled: boolean): boolean {
  const [active, setActive] = useState(false);
  useEffect(() => {
    if (!enabled || typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;
    let lock: WakeLockSentinel | null = null;
    let stopped = false;
    const request = async () => {
      try {
        const l = await navigator.wakeLock.request('screen');
        if (stopped) {
          void l.release();
          return;
        }
        lock = l;
        setActive(true);
        l.addEventListener('release', () => setActive(false));
      } catch {
        setActive(false); // refused (battery saver, hidden tab): the tablet's display setting is the fallback
      }
    };
    void request();
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !stopped) void request();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', onVisible);
      void lock?.release();
    };
  }, [enabled]);
  return active;
}

/* ── Chimes (Web Audio, no assets) ──────────────────────────────────────────────────────── */

/** The tickets this page has already announced, per station (ADR-045): one chime per new ticket. */
export const kdsHeard = createHeardRegistry();

let audioCtx: AudioContext | null = null;
function audio(): AudioContext | null {
  if (typeof window === 'undefined' || !('AudioContext' in window)) return null;
  audioCtx ??= new AudioContext();
  if (audioCtx.state === 'suspended') void audioCtx.resume();
  return audioCtx;
}
function tone(
  ac: AudioContext,
  freq: number,
  at: number,
  dur: number,
  gain = 0.2,
  type: OscillatorType = 'sine',
) {
  const o = ac.createOscillator();
  const g = ac.createGain();
  o.type = type;
  o.frequency.value = freq;
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(gain, at + 0.015);
  g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  o.connect(g).connect(ac.destination);
  o.start(at);
  o.stop(at + dur + 0.05);
}
/** A rising two-note chime for a new ticket. */
export function playNewTicket() {
  const ac = audio();
  if (!ac) return;
  const t = ac.currentTime;
  tone(ac, 880, t, 0.18);
  tone(ac, 1320, t + 0.16, 0.3);
}
/** A distinct low triple buzz for a cancellation (refinement 2). */
export function playCancelled() {
  const ac = audio();
  if (!ac) return;
  const t = ac.currentTime;
  for (let i = 0; i < 3; i++) tone(ac, 311, t + i * 0.22, 0.16, 0.22, 'square');
}
/** Warm the audio context inside a user gesture (the sound toggle). */
export function primeAudio() {
  audio();
}

const SOUND_KEY = 'didto-os-kds-sound';
export function readSoundChoice(): boolean | null {
  try {
    const v = localStorage.getItem(SOUND_KEY);
    return v === 'on' ? true : v === 'off' ? false : null;
  } catch {
    return null;
  }
}
export function writeSoundChoice(on: boolean) {
  try {
    localStorage.setItem(SOUND_KEY, on ? 'on' : 'off');
  } catch {
    // private mode: the choice lasts for this page only
  }
}

/* ── Actions: straight to the server, explicit retry with the same command id ──────────── */

export type KdsKind =
  | 'item.start'
  | 'item.ready'
  | 'item.undo'
  | 'item.recall'
  | 'ticket.start_all'
  | 'ticket.ready_all'
  | 'ticket.undo'
  | 'ticket.recall'
  | 'ticket.dismiss';

export type { ActionOutcome, PendingAction } from '@/app/directActions';

/**
 * Owner refinement 1: no hidden mutation queue. A command that cannot be confirmed stays on the
 * card as "Action not confirmed" until the employee retries it (same command id, so the server
 * replays instead of applying twice) or discards it. Shared with the Expo board (Phase 6).
 */
export function useKdsActions(locationId: string | null) {
  return useDirectActions(locationId, KDS_INVALIDATE);
}
const KDS_INVALIDATE: readonly (readonly string[])[] = [['kds'], ['orders'], ['expo']];
